import { skipToken } from '@tanstack/react-query';
import BacktestQuarantineNotice from '@/components/Trade/BacktestQuarantineNotice';
import BacktestOptionLiquidity from '@/components/Trade/BacktestOptionLiquidity';
import React, { useState, useEffect, useMemo, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from '@/components/Layout/AdminLayout';
import Alert from '@/components/UI/Alert';
import Card from '@/components/UI/Card';
import DataTable from '@/components/UI/DataTable';
import TradeHeader from '@/components/Trade/TradeHeader';
import TradeChart from '@/components/Chart/TradeChart';
import { renderTransactionOutput } from '@/components/Trade/TradeRenderUtils';
import Button from '@/components/UI/Button';
import './BacktestTrades.tailwind.css';

import type { BarOHLCVDetailed, OptionBuyingStrategy, EntityOptionObjectCompressed } from '@einhaus/project314-types';
import type { TradingStrategyBacktestDetailed as BacktestDetailed, TradingStrategyBacktestTrade as BacktestTrade } from '@/types/appRouter';
import FormInput from '@/components/UI/FormInput.tsx';
import EntityLink from '@/components/Entity/EntityLink.tsx';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { trpc } from '@/api/trpc.ts';
import { handleTrpcQuery } from '@/api/trpcResponseHandler';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useTradingRequisites } from '@/hooks/useTradingRequisites';
import { useFetchOptions } from '@/hooks/useFetchOptions';
import { getDateFromUnix } from '@einhaus/meteoric-helpers';
import TradeLogicV2ExitSummary from '@/components/TradeLogic/TradeLogicV2ExitSummary';

const BacktestTrades = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const backtestId = Number(searchParams.get('backtestId') || '0');
    const [inputBacktestId, setInputBacktestId] = useState('');

    // Auth state is handled by AdminLayout
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (inputBacktestId.trim()) {
            // Use setSearchParams to update the URL without navigating away
            setSearchParams({ backtestId: inputBacktestId.trim() });
        }
    };

    const [currentPage, setCurrentPage] = useState(1);
    const tradesPerPage = 10;

    // Chart data state
    const [loadingCharts, setLoadingCharts] = useState(false);
    const [chartError, setChartError] = useState<string | null>(null);
    const [chartData, setChartData] = useState<{ [key: string]: BarOHLCVDetailed[] }>({});

    // Fetch backtest with tRPC
    const backtestQuery = useAppQuery(
        {
            ...trpc.backtest.fetchBacktest.queryOptions({
                backtestId,
                shouldFetchTrades: true
            }),
            enabled: backtestId > 0
        },
        {
            fallbackErrorMessage: 'Failed to fetch backtest',
            showErrorToast: true,
            setError: (msg) => setError(msg)
        }
    );

    // Extract backtest data
    const backtest: BacktestDetailed | null = useMemo(() => backtestQuery.data?.backtest || null, [backtestQuery.data?.backtest]);

    // Extract trades data
    const filteredTrades: BacktestTrade[] = useMemo(
        () => backtest?.trades.filter((trade) => trade.didEnterTrade) || [],
        [backtest?.trades]
    );

    // Calculate total pages
    const totalPages = useMemo(() => Math.ceil(filteredTrades.length / tradesPerPage), [filteredTrades.length, tradesPerPage]);

    // Fetch entity details with tRPC if we have a backtest with entity info
    const entityQuery = useAppQuery(
        {
            ...trpc.entity.details.queryOptions(
                backtest && backtest.entityType !== 'FuturesProduct'
                    ? { entityId: backtest.entityId, entityType: backtest.entityType }
                    : skipToken
            )
        },
        {
            fallbackErrorMessage: 'Failed to fetch entity details',
            showErrorToast: true,
            setError: (msg) => setError(msg)
        }
    );

    // Extract entity data
    const entity = useMemo(() => entityQuery.data?.entity || null, [entityQuery.data?.entity]);

    // Fetch trading requisites with tRPC
    const { isLoading: isLoadingRequisites, requisites } = useTradingRequisites({
        enabled: backtestId > 0,
        errorConfig: {
            fallbackErrorMessage: 'Failed to fetch trading requisites',
            showErrorToast: true,
            setError: (msg) => setError(msg)
        }
    });

    const optionBuyingStrategies = useMemo(() => requisites?.optionBuyingStrategies || [], [requisites?.optionBuyingStrategies]);

    // Extract options to fetch
    const optionsToFetch = useMemo(() => {
        if (!filteredTrades.length) return [];

        const optionsMap = new Map<string, EntityOptionObjectCompressed>();

        for (const trade of filteredTrades) {
            for (const transaction of trade.transactions) {
                const optionId = transaction.derivativeId;
                const { derivativeType } = transaction;

                if (trade.entityType !== 'Asset' && trade.entityType !== 'AssetIndex') continue;
                if (!optionId || derivativeType !== 'Option') continue;

                const optionKey = `${optionId}_${trade.entityId}_${trade.entityType}`;

                if (!optionsMap.has(optionKey)) {
                    optionsMap.set(optionKey, {
                        oId: optionId,
                        eId: trade.entityId,
                        eType: trade.entityType
                    });
                }
            }
        }

        return Array.from(optionsMap.values());
    }, [filteredTrades]);

    const { options, isLoading: optionsLoading } = useFetchOptions(optionsToFetch);

    // Get current page trades
    const currentPageTrades = useMemo(() => {
        const startIndex = (currentPage - 1) * tradesPerPage;
        const endIndex = startIndex + tradesPerPage;
        return filteredTrades.slice(startIndex, endIndex);
    }, [filteredTrades, currentPage, tradesPerPage]);

    // Calculate chart data parameters
    const chartDataParams = useMemo(() => {
        if (filteredTrades.length === 0 || !entity || !backtest?.strategy) return null;

        const { strategy } = backtest;
        if (!strategy) return null;

        // Get the earliest and latest trade dates from ALL trades
        const allTradeDates = filteredTrades.map((trade: BacktestTrade) => new Date(trade.dateTime).getTime() / 1000);
        const earliestTradeDate = Math.min(...allTradeDates);

        // Get latest transaction date from ALL trades to ensure we capture all exit data
        let latestTransactionDate = earliestTradeDate;

        filteredTrades.forEach((trade: BacktestTrade) => {
            trade.transactions.forEach((transaction) => {
                const transactionTimestamp = new Date(transaction.dateTime).getTime() / 1000;

                if (transactionTimestamp > latestTransactionDate) {
                    latestTransactionDate = transactionTimestamp;
                }
            });
        });

        // Add a 7-day buffer on each side to ensure we have enough context
        const bufferDays = 7;
        const startDateTime = getDateFromUnix(earliestTradeDate - bufferDays * 24 * 60 * 60, 'ymdhms');
        const endDateTime = getDateFromUnix(latestTransactionDate + bufferDays * 24 * 60 * 60, 'ymdhms');

        return {
            entityId: entity.id,
            entityType: entity.entityType,
            duration: strategy.duration || '30m',
            startDateTime,
            endDateTime,
            shouldRemoveAfterHours: strategy.afterHoursRemoved && entity.entityType !== 'Cryptocurrency'
        };
    }, [filteredTrades, entity, backtest]);

    // Fetch chart data with tRPC
    const chartDataQuery = useAppQuery({
        ...trpc.entity.fetchBarsForDuration.queryOptions(
            chartDataParams
                ? {
                      entityId: chartDataParams.entityId,
                      entityType: chartDataParams.entityType,
                      duration: chartDataParams.duration,
                      startDateTime: chartDataParams.startDateTime,
                      endDateTime: chartDataParams.endDateTime,
                      shouldRemoveAfterHours: chartDataParams.shouldRemoveAfterHours
                  }
                : {
                      entityId: 0,
                      entityType: 'Asset',
                      duration: '1d',
                      startDateTime: new Date().toISOString(),
                      endDateTime: new Date().toISOString()
                  }
        ),
        enabled: !!chartDataParams
    });

    // Handle chart data loading and errors
    useEffect(() => {
        setLoadingCharts(chartDataQuery.isLoading);

        if (chartDataQuery.error) {
            handleTrpcQuery(chartDataQuery, {
                fallbackErrorMessage: 'Failed to fetch chart data',
                showErrorToast: true,
                setError: (msg) => setChartError(msg)
            });
        } else if (chartDataQuery.data?.bars && entity) {
            // Store the bars data
            setChartData({
                [`${entity.id}_${entity.entityType}`]: chartDataQuery.data.bars
            });
        }
    }, [chartDataQuery.error, chartDataQuery.data?.bars, chartDataQuery?.isLoading, entity]);

    // Handle pagination
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    // Determine if data is loading
    const isLoading =
        backtestQuery.isLoading ||
        entityQuery.isLoading ||
        isLoadingRequisites ||
        chartDataQuery?.isLoading ||
        (optionsToFetch.length > 0 && optionsLoading);

    // Show loading spinner while data is loading (authentication is handled by AdminLayout)
    if (isLoading) {
        return (
            <AdminLayout containerSize="2200px" title="Backtest Trades">
                <div className="flex justify-center my-5">
                    <DataLoadingSpinner text="Loading backtest trades..." />
                </div>
            </AdminLayout>
        );
    }

    if (error) {
        return (
            <AdminLayout containerSize="2200px" title="Backtest Trades">
                <Alert variant="danger">{error}</Alert>
            </AdminLayout>
        );
    }

    // If no backtestId is provided, show the input form
    if (backtestId === 0) {
        return (
            <AdminLayout containerSize="2200px" title="Backtest Trades">
                <Card className="mb-4">
                    <div className="p-4">
                        <h4 className="mb-3">Enter Backtest ID</h4>
                        <form onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <label className="block text-sm font-medium mb-1" htmlFor="backtestId">
                                    Backtest ID
                                </label>
                                <FormInput
                                    required
                                    id="backtestId"
                                    placeholder="Enter backtest ID"
                                    type="text"
                                    value={inputBacktestId}
                                    onChange={(e) => setInputBacktestId(e.target.value)}
                                />
                                <div className="mt-1 text-sm text-zinc-400">
                                    Enter a backtest ID to view its trades and performance details
                                </div>
                            </div>
                            <Button type="submit" variant="primary">
                                View Backtest Trades
                            </Button>
                        </form>
                    </div>
                </Card>
            </AdminLayout>
        );
    }

    if (!backtest) {
        return (
            <AdminLayout containerSize="2200px" title="Backtest Trades">
                <Alert variant="warning">No backtest data found</Alert>
            </AdminLayout>
        );
    }

    const {
        id,
        profit,
        percentageGain,
        medianProfitPercent,
        avgProfitPerDayPercent,
        optionBuyingStrategyIdLeg1,
        optionBuyingStrategyIdLeg2,
        optionBuyingStrategyIdLeg3,
        optionBuyingStrategyIdLeg4,
        optionBuyingStrategyIdLeg5,
        optionBuyingStrategyIdLeg6
    } = backtest;

    const optionBuyingStrategyIds = [
        optionBuyingStrategyIdLeg1,
        optionBuyingStrategyIdLeg2,
        optionBuyingStrategyIdLeg3,
        optionBuyingStrategyIdLeg4,
        optionBuyingStrategyIdLeg5,
        optionBuyingStrategyIdLeg6
    ];

    return (
        <AdminLayout containerSize="2200px" title="Backtest Trades">
            <BacktestQuarantineNotice quarantine={backtest.quarantine} />
            {backtest.entityType === 'FuturesProduct' && !backtest.quarantine && (
                <p className="mb-4 text-sm text-zinc-400">
                    Futures trade percentages are net price returns. Return on the configured research reserve is shown separately in the
                    backtest summary.
                </p>
            )}
            <Card className="mb-4">
                <div className="p-4">
                    <DataTable
                        striped
                        columns={[
                            { header: 'Backtest ID', accessorKey: 'id', size: 140 },
                            {
                                header: 'Entity',
                                accessorKey: 'entity',
                                cell: (info) => info.getValue()
                            },
                            { header: 'Strategy', accessorKey: 'strategy' },
                            {
                                header: 'Trade Logic V2 Exit',
                                accessorKey: 'exitLogic',
                                cell: () => (
                                    <TradeLogicV2ExitSummary
                                        className="mb-0 bg-transparent p-0"
                                        exitCandidateHash={backtest?.exitCandidateHash ?? null}
                                        exitCandidateId={backtest?.exitCandidateId ?? null}
                                        showCandidateInfo={false}
                                        strategy={backtest?.strategy ?? null}
                                    />
                                )
                            },
                            {
                                header: 'Option Strategies',
                                accessorKey: 'optionBuyingStrategyIds',
                                cell: (info) => {
                                    const strategyIds = info.getValue() as number[];

                                    const strategies = strategyIds
                                        .map((id) => optionBuyingStrategies.find((s) => s.id === id))
                                        .filter(Boolean);

                                    return (
                                        <div>
                                            {strategies.map((strategy) => (
                                                <div key={strategy?.id}>{strategy?.name}</div>
                                            ))}
                                        </div>
                                    );
                                }
                            },
                            {
                                header: 'Profit',
                                accessorKey: 'profit',
                                cell: (info) => {
                                    // Get the value safely
                                    const rawValue = info.getValue();
                                    const valueStr = typeof rawValue === 'string' ? rawValue : '';
                                    const value = parseFloat(valueStr.replace('$', ''));
                                    const colorClass = value > 0 ? 'text-profit-green !text-[#29a740]' : value < 0 ? 'text-red-500' : '';
                                    return <span className={colorClass}>{valueStr}</span>;
                                }
                            },
                            {
                                header: 'Profit %',
                                accessorKey: 'profitPercentage',
                                cell: (info) => {
                                    // Get the value safely
                                    const rawValue = info.getValue();
                                    const valueStr = typeof rawValue === 'string' ? rawValue : '';
                                    const value = parseFloat(valueStr.replace('%', ''));
                                    const colorClass = value > 0 ? 'text-profit-green !text-[#29a740]' : value < 0 ? 'text-red-500' : '';
                                    return <span className={colorClass}>{valueStr}</span>;
                                }
                            },
                            {
                                header: 'Median trade %',
                                accessorKey: 'medianProfitPercent',
                                cell: (info) => {
                                    const rawValue = info.getValue();
                                    const value = typeof rawValue === 'number' ? rawValue : 0;
                                    const colorClass = value > 0 ? 'text-profit-green !text-[#29a740]' : value < 0 ? 'text-red-500' : '';
                                    const display = `${value.toFixed(2)}%`;
                                    return <span className={colorClass}>{display}</span>;
                                }
                            },
                            {
                                header: 'Avg profit/day %',
                                accessorKey: 'avgProfitPerDayPercent',
                                cell: (info) => {
                                    const rawValue = info.getValue();
                                    const value = typeof rawValue === 'number' ? rawValue : 0;
                                    const colorClass = value > 0 ? 'text-profit-green !text-[#29a740]' : value < 0 ? 'text-red-500' : '';
                                    const display = `${value.toFixed(2)}%`;
                                    return <span className={colorClass}>{display}</span>;
                                }
                            }
                        ]}
                        data={[
                            {
                                id,
                                entity: entity ? <EntityLink showName showSymbol entity={entity} /> : 'Unknown',
                                strategy: backtest.strategy?.name || 'Unknown',
                                exitLogic: null,
                                optionBuyingStrategyIds,
                                profit: `$${profit}`,
                                profitPercentage: `${percentageGain}%`,
                                medianProfitPercent,
                                avgProfitPerDayPercent
                            }
                        ]}
                        search={false}
                    />
                </div>
            </Card>

            {filteredTrades.length === 0 ? (
                <Alert variant="info">No trades found for this backtest</Alert>
            ) : (
                <>
                    <div className="mb-4">
                        <p className="text-zinc-400">
                            Showing {currentPageTrades.length} of {filteredTrades.length} trades
                        </p>
                    </div>
                    <BacktestOptionLiquidity
                        key={`${backtestId}-${currentPage}`}
                        backtestId={backtestId}
                        tradeIds={currentPageTrades.map((trade) => trade.id)}
                    />
                    {currentPageTrades.map((trade: BacktestTrade) => {
                        const optionBuyingStrategiesForTrade: OptionBuyingStrategy[] = [];

                        trade.transactions.forEach((transaction) => {
                            if (optionBuyingStrategiesForTrade.find((obs) => obs.id === transaction.optionBuyingStrategyId)) return;

                            if (transaction.optionBuyingStrategyId) {
                                const foundStrategy = optionBuyingStrategies.find((s) => s.id === transaction.optionBuyingStrategyId);
                                if (foundStrategy) optionBuyingStrategiesForTrade.push(foundStrategy);
                            }
                        });

                        return (
                            <div className="mb-5" key={trade.id}>
                                <TradeHeader
                                    entity={entity}
                                    optionBuyingStrategies={optionBuyingStrategiesForTrade}
                                    options={options}
                                    strategy={backtest?.strategy ?? null}
                                    trade={trade}
                                />

                                {/* Exit Logic Summary */}
                                <TradeLogicV2ExitSummary
                                    exitCandidateHash={backtest?.exitCandidateHash ?? null}
                                    exitCandidateId={backtest?.exitCandidateId ?? null}
                                    strategy={backtest?.strategy ?? null}
                                />

                                <Card>
                                    {loadingCharts ? (
                                        <div className="text-center py-4">
                                            <DataLoadingSpinner text="Loading chart data..." />
                                        </div>
                                    ) : chartError ? (
                                        <div className="text-center text-red-500 py-4">{chartError}</div>
                                    ) : chartData[`${entity?.id}_${entity?.entityType}`] &&
                                      chartData[`${entity?.id}_${entity?.entityType}`].length > 0 ? (
                                        <>
                                            <TradeChart
                                                showVolume
                                                bars={chartData[`${entity?.id}_${entity?.entityType}`]}
                                                duration={backtest.strategy?.duration || '1d'}
                                                entity={entity}
                                                height={700}
                                                trade={trade}
                                            />
                                        </>
                                    ) : (
                                        <div className="text-center text-zinc-400 py-4">No chart data available for this trade</div>
                                    )}
                                </Card>

                                <Card className="mt-3">
                                    <h5 className="mb-3 px-4 pt-4">Transactions</h5>
                                    <div className="px-4 pb-4">
                                        {renderTransactionOutput({
                                            transactions: trade.transactions,
                                            possibleOptions: new Map(
                                                options.map((option) => [`${option.id}_${option.assetId}_${option.entityType}`, option])
                                            )
                                        })}
                                    </div>
                                </Card>
                            </div>
                        );
                    })}
                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex justify-center mt-4">
                            <nav aria-label="Backtest trades pagination">
                                <ul className="flex space-x-1">
                                    <li className={`${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                        <button
                                            className="px-3 py-2 bg-zinc-800 border border-zinc-700 text-blue-400 hover:bg-blue-500 hover:text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                            disabled={currentPage === 1}
                                            onClick={() => handlePageChange(1)}
                                        >
                                            First
                                        </button>
                                    </li>
                                    <li className={`${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                        <button
                                            className="px-3 py-2 bg-zinc-800 border border-zinc-700 text-blue-400 hover:bg-blue-500 hover:text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                            disabled={currentPage === 1}
                                            onClick={() => handlePageChange(currentPage - 1)}
                                        >
                                            Previous
                                        </button>
                                    </li>
                                    {/* Show limited page numbers with ellipses when there are many pages */}
                                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                                        .filter((page) => {
                                            // Always show first and last 2 pages
                                            if (page <= 2 || page > totalPages - 2) return true;
                                            // Always show current page and 1 page before and after
                                            if (Math.abs(page - currentPage) <= 1) return true;
                                            // Hide other pages
                                            return false;
                                        })
                                        .map((page, index, filteredPages) => {
                                            // Add ellipsis if there's a gap between consecutive pages
                                            const needsEllipsisBefore = index > 0 && page - filteredPages[index - 1] > 1;

                                            return (
                                                <React.Fragment key={page}>
                                                    {needsEllipsisBefore && (
                                                        <li className="opacity-50 cursor-not-allowed">
                                                            <button
                                                                disabled
                                                                className="px-3 py-2 bg-zinc-800 border border-zinc-700 text-zinc-400 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                ...
                                                            </button>
                                                        </li>
                                                    )}
                                                    <li className={`${currentPage === page ? 'active' : ''}`}>
                                                        <button
                                                            className={`px-3 py-2 border border-zinc-700 rounded ${
                                                                currentPage === page
                                                                    ? 'bg-blue-600 text-white'
                                                                    : 'bg-zinc-800 text-blue-400 hover:bg-blue-500 hover:text-white'
                                                            }`}
                                                            onClick={() => handlePageChange(page)}
                                                        >
                                                            {page}
                                                        </button>
                                                    </li>
                                                </React.Fragment>
                                            );
                                        })}
                                    <li className={`${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                        <button
                                            className="px-3 py-2 bg-zinc-800 border border-zinc-700 text-blue-400 hover:bg-blue-500 hover:text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                            disabled={currentPage === totalPages}
                                            onClick={() => handlePageChange(currentPage + 1)}
                                        >
                                            Next
                                        </button>
                                    </li>
                                    <li className={`${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                        <button
                                            className="px-3 py-2 bg-zinc-800 border border-zinc-700 text-blue-400 hover:bg-blue-500 hover:text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                            disabled={currentPage === totalPages}
                                            onClick={() => handlePageChange(totalPages)}
                                        >
                                            Last
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    )}
                </>
            )}
        </AdminLayout>
    );
};

export default BacktestTrades;
